/**
 * AssignmentChip.jsx
 * Modular component for rendering assignment tiles in the calendar.
 * Implements priority-based display logic and premium iOS-inspired aesthetics.
 */

import React, { useMemo, useState } from 'react';
import './AssignmentChip.css';
import { formatTime, formatDateSimple } from '../../../../../utils/dateUtils';

/**
 * AssignmentChip Component
 * 
 * @param {Object} props
 * @param {Object} props.assignment - The assignment data object
 * @param {string} props.mode - 'timeline-timed', 'timeline-allday', or 'month'
 * @param {boolean} props.hasConflict - Whether to show the conflict icon
 * @param {Object} props.style - Inline styles for positioning (left, top, width, height)
 * @param {Object} [props.interaction] - Current interaction state (dragging/resizing)
 * @param {Function} [props.onClick] - Click handler
 * @param {Function} [props.onMouseDown] - Mouse down handler (for starting drag)
 * @param {Function} [props.onMouseEnter] - Hover enter handler
 * @param {Function} [props.onMouseLeave] - Hover leave handler
 * @param {Function} [props.onContextMenu] - Context menu handler
 * @param {Function} [props.onDrop] - Universal drop handler for resources/assignments
 * @param {number} [props.scrollX=0] - Current horizontal scroll position (for sticky labels)
 * @param {boolean} [props.isConflictDetectionEnabled=true] - Global toggle
 * @returns {JSX.Element}
 */
export default function AssignmentChip({
    assignment: a,
    mode = 'timeline-timed',
    hasConflict = false,
    style = {},
    interaction = null,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onContextMenu,
    onDragStart,
    onDragEnd,
    onDrop,
    scrollX = 0,
    isConflictDetectionEnabled = true
}) {
    const [isDragOverAssignment, setIsDragOverAssignment] = useState(false);
    
    const isInteracting = String(interaction?.assignment?.id) === String(a.id) && interaction.moved && !a.isGhost;
    const isGhost = a.isGhost;
    const status = (a.status || 'aktiv').toLowerCase();
    
    // Width/Height logic for content visibility
    const width = parseFloat(style.width) || 0;
    const height = parseFloat(style.height) || 0;

    // Thresholds
    const isTimeline = mode.startsWith('timeline');
    const isTimed = mode === 'timeline-timed';
    const isAllday = mode === 'timeline-allday';
    const isMonth = mode === 'month';

    const isMicro = isTimed && width < 42;
    const isStacked = isTimed && height > 38;
    const isTripleStacked = isTimed && height > 54;
    
    // Decoupled Multi-Dimensional Visibility Logic
    // We check if an element fits based on the "Line Count" it would require.
    
    // Prio 1: Conflict (always visible)
    
    // Prio 2: Name
    // Needs at least 1 line (22px) and some width
    const canShowName = isMonth || (isTimed && width > 50 && height > 22) || (isAllday && height > 40);
    
    // Prio 3: Resources
    // Fits if:
    // - Enough width to stay in Line 1 (width > 140)
    // - Enough height to move to Line 2 (height > 44)
    const canShowResources = canShowName && (
        (isMonth && width > 120) ||
        (isAllday && height > 160) || // Lowered for allday
        (isTimed && (width > 140 || height > 44))
    );
    
    // Prio 4: Time
    // Fits if:
    // - Enough width to stay in Line 1 (width > 240)
    // - Enough height to move to Line 3 (height > 66)
    // - Mixed: width > 140 (Line 2 fits) AND height > 44
    const canShowTime = canShowResources && isTimed && (
        (width > 260) || 
        (height > 66) ||
        (width > 140 && height > 44)
    );
    
    // Prio 5: Date
    const canShowDate = canShowName && (
        (isMonth && width > 150) || (isAllday && height > 240) // Lowered for allday
    );

    // Sticky Label Calculation (only for wide items in timed view)
    const stickyStyle = useMemo(() => {
        if (!isTimed || isMicro || scrollX <= 0) return {};
        const left = parseFloat(style.left) || 0;
        const offset = Math.max(0, scrollX - left);
        const clampedOffset = Math.min(offset, width - 60);
        return { paddingLeft: clampedOffset + 8 };
    }, [isTimed, isMicro, scrollX, style.left, width]);

    // Formatters
    const timeStr = useMemo(() => {
        if (!a.start_datetime || !a.end_datetime) return '';
        const s = new Date(a.start_datetime);
        const e = new Date(a.end_datetime);
        return canShowTime ? `${formatTime(s)} - ${formatTime(e)}` : formatTime(s);
    }, [a.start_datetime, a.end_datetime, canShowTime]);

    const dateRangeStr = useMemo(() => {
        return formatDateSimple(new Date(a.start_datetime), new Date(a.end_datetime));
    }, [a.start_datetime, a.end_datetime]);

    return (
        <div
            className={`assignment-chip mode-${mode} status-${status} ${isInteracting ? 'is-dragging' : ''} ${isDragOverAssignment ? 'is-drag-over' : ''} ${isGhost ? 'is-preview-ghost' : ''} ${isStacked ? 'is-stacked' : ''}`}
            style={style}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={(e) => {
                if (isGhost) return;
                e.preventDefault();
                e.stopPropagation();
                
                // Only show target highlight for external drags (e.g. sidebar resources)
                // If we are currently moving an assignment, interaction will be non-null.
                if (!interaction) {
                    setIsDragOverAssignment(true);
                }
            }}
            onDragLeave={(e) => {
                e.stopPropagation();
                setIsDragOverAssignment(false);
            }}
            onDrop={(e) => {
                if (isGhost) return;
                e.preventDefault();
                e.stopPropagation();
                setIsDragOverAssignment(false);
                onDrop?.(e, new Date(a.start_datetime), a);
            }}
            draggable={!isGhost}
        >
            {/* Resize Handles (only for timeline mode) */}
            {isTimeline && !isGhost && (
                <>
                    {isTimed && <div className="resize-handle left" onMouseDown={(e) => { e.stopPropagation(); onMouseDown?.(e, 'resize-left'); }} />}
                    {isTimed && <div className="resize-handle right" onMouseDown={(e) => { e.stopPropagation(); onMouseDown?.(e, 'resize-right'); }} />}
                    {isAllday && <div className="resize-handle top" onMouseDown={(e) => { e.stopPropagation(); onMouseDown?.(e, 'resize-top'); }} />}
                    {isAllday && <div className="resize-handle bottom" onMouseDown={(e) => { e.stopPropagation(); onMouseDown?.(e, 'resize-bottom'); }} />}
                </>
            )}

            <div className="chip-content-wrapper">
                {/* --- Allday Specialized Layout (Direct vertical stack) --- */}
                {isAllday ? (
                    <>
                        {isConflictDetectionEnabled && hasConflict && (
                            <div className="chip-priority-1-conflict">⚠️</div>
                        )}
                        {canShowName && (
                            <div className="chip-priority-3-name">{a.project_name}</div>
                        )}
                        {canShowResources && (a.workers?.length > 0 || a.vehicles?.length > 0) && (
                            <div className="chip-priority-4-resources">
                                {a.workers?.length > 0 && (
                                    <div className="resource-pill">
                                        <span className="resource-icon">👤</span>
                                        <span className="resource-count">{a.workers.length}</span>
                                    </div>
                                )}
                                {a.vehicles?.length > 0 && (
                                    <div className="resource-pill">
                                        <span className="resource-icon">🚚</span>
                                        <span className="resource-count">{a.vehicles.length}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {canShowDate && <div className="chip-priority-6-date">{dateRangeStr}</div>}
                    </>
                ) : (
                    /* --- Standard / Timed Layout (With isolated sticky header) --- */
                    <>
                        <div className="chip-id-header" style={stickyStyle}>
                            {isConflictDetectionEnabled && hasConflict && (
                                <div className="chip-priority-1-conflict" aria-label="Konflikt">⚠️</div>
                            )}
                            {isMonth && (
                                <div className="chip-priority-2-type">{a.all_day ? '📅' : '🕒'}</div>
                            )}
                            {!isMicro && canShowName && (
                                <div className="chip-priority-3-name">{a.project_name}</div>
                            )}
                        </div>

                        {!isMicro && canShowResources && (a.workers?.length > 0 || a.vehicles?.length > 0) && (
                            <div className="chip-priority-4-resources">
                                {a.workers?.length > 0 && <div className="resource-pill">👤 {a.workers.length}</div>}
                                {a.vehicles?.length > 0 && <div className="resource-pill">🚚 {a.vehicles.length}</div>}
                            </div>
                        )}

                        {!isMicro && isTimed && canShowTime && (
                           <div className="chip-priority-5-time">{timeStr}</div>
                        )}

                        {!isMicro && isMonth && canShowDate && (
                            <div className="chip-priority-6-date">{a.all_day ? '' : dateRangeStr}</div>
                        )}

                        {!isMicro && isTripleStacked && a.location && (
                            <div className="chip-location">📍 {a.location}</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
